import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Search2 from '../../pictures/search-2.svg';
import SelectedIcon from '../../pictures/icon-selected.svg';

import * as style from './SelectSearch.module.scss';

const iconEnum = {
  'search-2': Search2,
};

function SelectSearch({
  style: customStyle = {},
  iconRight,
  value,
  handleChange,
  handleClickOption,
  handleFocus,
  handleBlur,
  connectedField,
  placeholder,
  list,
  name,
  handleToggleOptions,
  disabled,
  type,
  error,
  id,
}) {
  const getRightIcon = (icon) => {
    if (icon && typeof icon === 'string') return iconEnum[icon];
    if (icon && typeof icon !== 'string') return icon;
    return null;
  };
  const RightIcon = getRightIcon(iconRight);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const getValueIndex = (l, v) => l?.findIndex((el) => el.value === v);

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(getValueIndex(list, value));
  const [inputValue, setInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(list);
  const [isFirstTimeSetup, setIsFirstTimeSetup] = useState(true);

  const resetInputValue = () => {
    if (selectedOption !== -1) {
      setInputValue(filteredOptions[selectedOption]?.title || '');
    } else {
      setInputValue('');
    }
  };

  const clearSelectedOption = () => {
    setSelectedOption(-1);
    handleChange(name, '');
  }

  const setSelectedThenCloseDropdown = (index, title) => {
    setSelectedOption(index);
    setIsOptionsOpen(false);
    handleChange(name, filteredOptions[index]?.value);
    setInputValue(title);
  };

  const handleClickOutside = (event) => {
    if (ref && ref.current && !ref.current.contains(event.target)) {
      if (!inputValue?.length) {
        clearSelectedOption();
      } else if (inputValue !== (filteredOptions[selectedOption]?.title || '')) {
        resetInputValue();
      }
      setIsOptionsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [inputValue, filteredOptions, selectedOption]);

  useEffect(() => {
    if (value && value !== selectedOption) {
      const ind = getValueIndex(list, value);
      setSelectedOption(ind);
    } else if (!value) {
      setSelectedOption(-1);
    }
  }, [value, list]);

  useEffect(() => {
    if (connectedField) {
      setInputValue('');
      setSelectedOption(-1);
    }
  }, [connectedField]);

  useEffect(() => {
    setFilteredOptions(
      list.filter(option =>
        (inputValue?.length ? !option.component : true) && option.title.toLowerCase().includes(inputValue.toLowerCase())
      )
    );
  }, [inputValue, list]);

  useEffect(() => {
    if (isFirstTimeSetup && filteredOptions[selectedOption]?.title) {
      setInputValue(filteredOptions[selectedOption]?.title);
      setIsFirstTimeSetup(false);
    }
  }, [isFirstTimeSetup, selectedOption, filteredOptions]);

  // New useEffect to update selectedOption when filteredOptions change
  useEffect(() => {
    const newIndex = getValueIndex(filteredOptions, value);
    setSelectedOption(newIndex);
  }, [filteredOptions, value]);

  const handleKeyDown = (index) => (e) => {
    switch (e.key) {
      case ' ':
      case 'SpaceBar':
      case 'Enter':
        e.preventDefault();
        setSelectedThenCloseDropdown(index);
        break;
      default:
        break;
    }
  };

  const handleListKeyDown = (e) => {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setIsOptionsOpen(false);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedOption(
          selectedOption - 1 >= 0 ? selectedOption - 1 : filteredOptions.length - 1,
        );
        handleChange(name, filteredOptions[selectedOption - 1 >= 0 ? selectedOption - 1 : filteredOptions.length - 1].value);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setSelectedOption(
          selectedOption === filteredOptions.length - 1 ? 0 : selectedOption + 1,
        );
        handleChange(name, filteredOptions[selectedOption === filteredOptions.length - 1 ? 0 : selectedOption + 1].value);
        break;
      default:
        break;
    }
  };

  const handleFocusInput = () => {
    if (handleFocus) handleFocus();
  }

  const handleBlurInput = () => {
    if (handleBlur) handleBlur();
  }

  const handleInternalChange = (e) => {
    setInputValue(e.target.value);
  }

  return (
    <div
      className={cn(style.select, {
        [style.active]: isOptionsOpen,
        [style.error]: error,
        [style.text]: type === 'text',
        [style.textColor]: type === 'textColor',
        [style.buttonLike]: type === 'button'
      })}
      style={customStyle}
      id={id}
      ref={ref}
    >
      <input
        onFocus={handleFocusInput}
        onBlur={handleBlurInput}
        type="text"
        value={inputValue}
        ref={inputRef}
        onChange={handleInternalChange}
        onClick={() => setIsOptionsOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        className={style.input}
      />
      {(iconRight) ? (
        <div className={style.iconRightStatus}>
          <RightIcon />
        </div>
      ) : null}
      <ul
        className={cn(style.options, {
          [style.show]: isOptionsOpen,
          [style.top]: type === 'top',
        })}
        role="listbox"
        aria-activedescendant={filteredOptions && filteredOptions[selectedOption]}
        tabIndex={-1}
        onKeyDown={handleListKeyDown}
      >
        {filteredOptions?.map((option, index) => option.component ? (
          <li
            key={option.value}
            role="option"
            onClick={() => {
              if (handleClickOption) handleClickOption(option.value);
              setIsOptionsOpen(false);
            }}
          >
            {option.component}
          </li>
        ) : (
          <li
            className={cn({
              [style.delete]: option.red,
              [style.hidden]: option.hidden,
            })}
            key={option.value}
            role="option"
            aria-selected={selectedOption === index}
            tabIndex={0}
            onKeyDown={handleKeyDown(index)}
            onClick={() => {
              if (option.disabled) return;
              setSelectedThenCloseDropdown(index, option?.title);
            }}
            disabled={option.disabled}
          >
            {option.icon}
            <span>{option.title}</span>
            {selectedOption === index && (
              <div className={style.iconSelected}>
                <SelectedIcon />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

SelectSearch.propTypes = {
  type: PropTypes.string,
  value: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  handleFocus: PropTypes.func,
  handleBlur: PropTypes.func,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  list: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    title: PropTypes.string,
  })),
  disabled: PropTypes.bool,
  id: PropTypes.string,
  connectedField: PropTypes.string,
};

export default SelectSearch;